import { Button } from 'shared/ui/Button';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { SideModal, SideModalBody, SideModalFooter, useModalsActions } from 'shared/ui/SideModal';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { PendingIcon, type ZipValidationModel } from 'entities/campaign';
import { useContractorDateWithTimeZone } from 'shared/lib/timezones';

import { useZipsActions } from './UseZipsStore';

interface ZipsResultsModalProps {
  zipValidationData: ZipValidationModel;
  zipResults: JSX.Element;
  modalProps: {
    title: string;
    subtitle: string;
    buttonText: string;
    onSubmit?: () => void;
    autoTestAttributesName?: string;
  };
}

export function ZipsResultsModal({ zipValidationData, modalProps, zipResults }: ZipsResultsModalProps) {
  const { validCount, nextBillingStartAt } = zipValidationData;
  const { title, subtitle, buttonText, onSubmit, autoTestAttributesName } = modalProps;
  const { closeAllBesidesFirst, close } = useModalsActions();
  const { setZipsNumber } = useZipsActions();
  const { setNextBillingStartAt } = useZipsActions();
  const getPendingDate = useContractorDateWithTimeZone();

  const pendingDate = nextBillingStartAt && getPendingDate(nextBillingStartAt);

  const submit = () => {
    if (!onSubmit) {
      closeAllBesidesFirst();
      setZipsNumber(validCount ?? 0);
      setNextBillingStartAt(nextBillingStartAt);

      return;
    }

    onSubmit();
  };

  return (
    <SideModal autoTestAttributesName={autoTestAttributesName}>
      <SideModalBody
        title={title}
        className="pb-10"
      >
        <div className="text-sm">{subtitle}</div>
        <div className="mt-6 flex items-center justify-between">
          <div className="text-lg font-medium text-slate-900">Validation results</div>
          <Button
            className="w-25"
            onClick={close}
            type="button"
            variant="outline"
            size="medium"
          >
            Edit ZIPs
          </Button>
        </div>
        <div className="mt-6">{zipResults}</div>
      </SideModalBody>
      <SideModalFooter ignorePaddingsWidth>
        <div className="flex justify-center">
          <Button
            onClick={submit}
            disabled={!validCount}
            className="w-[340px]"
            {...getDataAutoTestAttributes(['button-update-zips'])}
          >
            <div className="flex items-center">
              {!!nextBillingStartAt && (
                <MaterialIcon
                  {...getDataAutoTestAttributes(['icon-pending'])}
                  className="mr-2 text-[20px] text-white"
                >
                  update
                </MaterialIcon>
              )}
              <div>{buttonText}</div>
            </div>
          </Button>
        </div>
        {!!nextBillingStartAt && (
          <div className="mt-[14px] flex items-start justify-center px-10">
            <PendingIcon />
            <span className="ml-2 mt-[2px] text-sm">ZIP Code changes will be applied on {pendingDate}.</span>
          </div>
        )}
      </SideModalFooter>
    </SideModal>
  );
}
